import type {
  ComponentType,
  FC,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  MouseEvent as ReactMouseEvent,
  Ref,
  RefAttributes,
} from 'react';
import { forwardRef } from 'react';

import type {
  ArticleCardProps,
  ButtonProps,
  IconButtonProps,
  LinkProps,
  TextButtonProps,
} from 'platform/bricks-next/types';
import { SystemError } from 'platform/Error';
import { useStalePageCheck } from 'platform/hooks/useReloadStalePage';
import { useToPath } from 'platform/preserveQueryParams';
import type { GestureResponderEvent } from 'react-native';
import { useHref, useLinkClickHandler } from 'react-router';
import { trackNavigationEvent } from 'shared/hooks/useTrackPerformance';

import type { SharedCTAProps } from './types';

type ActionProps =
  | LinkProps
  | TextButtonProps
  | IconButtonProps
  | ButtonProps
  | ArticleCardProps['primaryButtonProps']
  | NonNullable<ArticleCardProps['secondaryButtonProps']>;

/**
 * Function that checks if the href is the same origin as the current page.
 * This is useful for determining if a link should be opened via an anchor tag or a router link.
 */
const isSameOrigin = (href: string): boolean => {
  // Shortcircuit for relative URLs
  if (href.startsWith('/')) {
    return true;
  }

  const hrefUrl = new URL(href);
  return hrefUrl.origin === window.location.origin;
};

const isString = (value: unknown): value is string => typeof value === 'string';

function useInternalCTAProps({
  href: to,
  customDisallowedSearchParams,
  onPress: handlePress,
  replace,
  state,
  target,
}: Pick<
  ActionProps,
  'href' | 'customDisallowedSearchParams' | 'onPress' | 'replace' | 'state' | 'target'
>): { href?: string } & Pick<ActionProps, 'onPress'> {
  const checkStalePageAndAuth = useStalePageCheck();

  const getToPath = useToPath(customDisallowedSearchParams);
  const toPath = to ? getToPath(to) : {};
  const href = useHref(toPath);
  const handleRouterClick = useLinkClickHandler(toPath, {
    replace,
    state,
    target,
  });

  // If `to` is undefined or points to a link external to the app, return passed-in props
  // (Can't put this earlier in the function because hooks can't be called conditionally)
  if (!to || (isString(to) && !isSameOrigin(to))) {
    return { href: to, onPress: handlePress };
  }

  const onPress = (event: GestureResponderEvent) => {
    void checkStalePageAndAuth();

    trackNavigationEvent();
    handlePress?.(event);

    if (!event.defaultPrevented) {
      /**
       * both Bricks-Next's onPress and React Router's onClick must be invoked to ensure
       * cross-platform navigation interoperability. this is the simplest solution
       * without rewriting the whole nav system.
       */
      handleRouterClick(event as unknown as ReactMouseEvent<HTMLAnchorElement, MouseEvent>);
    }
  };

  return { href, onPress };
}

/**
 * Internal HOC that handles navigation via Bricks-Next's component using React Router's routing system. This component is used to handle navigation in a way that's compatible with both web and native.
 */
export function withInternalCTAHandling<T extends ActionProps>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Setting type as any since the HOC is generic
  WrappedComponent: ComponentType<any>,
): ForwardRefExoticComponent<
  PropsWithoutRef<T> & RefAttributes<HTMLAnchorElement | HTMLButtonElement>
> {
  return forwardRef<HTMLAnchorElement | HTMLButtonElement, T>(
    (
      {
        href: to,
        customDisallowedSearchParams,
        onPress: handlePress,
        replace,
        state,
        target,
        analyticsName,
        ...rest
      }: PropsWithoutRef<T>,
      ref,
    ) => {
      if (!to) {
        throw new SystemError(
          'Router navigation components must have a properly formatted `to` prop',
        );
      }

      const { href, onPress } = useInternalCTAProps({
        href: to,
        customDisallowedSearchParams,
        onPress: handlePress,
        replace,
        state,
        target,
      });

      return (
        <WrappedComponent
          {...rest}
          analyticsName={analyticsName}
          href={href}
          onPress={onPress}
          ref={ref as Ref<unknown>} // Cast the ref to the correct type
          target={target}
        />
      );
    },
  );
}

/**
 * Determines if a CTA should be opened via an anchor tag or a router link.
 * If the href is not the same origin as the current page, it will open via an anchor tag.
 * Otherwise, it will open via a router link.
 *
 * Both are styled via Bricks-Next
 * @throws Error if href is the same origin as the current page
 */
export function withCTAHandling<T extends SharedCTAProps>(
  /* eslint-disable @typescript-eslint/no-explicit-any -- Setting type as any since the HOC is generic */
  BricksNextComponent: ComponentType<any>,
  RouterComponent: ComponentType<any>,
  /* eslint-enable @typescript-eslint/no-explicit-any */
): FC<T> {
  return ({ href, ...rest }: T) => {
    // If href points to a link external to the app OR only uses onPress, use BricksNextComponent
    if (!href || (typeof href === 'string' && !isSameOrigin(href))) {
      // Validate that href for external links are not of the same origin
      return <BricksNextComponent href={href} {...rest} />;
    }

    // If href points to a link internal to the app, use RouterComponent
    return <RouterComponent href={href} {...rest} />;
  };
}
